import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class SolanaErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Solana Error Boundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className="solana-error-boundary"
          style={{
            padding: '20px',
            border: '1px solid #ff6b6b',
            borderRadius: '8px',
            backgroundColor: '#fff5f5',
            margin: '20px 0',
            fontFamily: 'Arial, sans-serif',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                backgroundColor: '#ff6b6b',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              ⚠️
            </div>
            <h2
              style={{
                margin: 0,
                color: '#d63031',
                fontSize: '18px',
                fontWeight: '600',
              }}
            >
              Solana Integration Error
            </h2>
          </div>

          <p
            style={{
              margin: '0 0 16px 0',
              color: '#636e72',
              fontSize: '14px',
              lineHeight: '1.5',
            }}
          >
            Something went wrong with the Solana blockchain integration. This
            might be due to:
          </p>

          <ul
            style={{
              margin: '0 0 16px 0',
              paddingLeft: '20px',
              color: '#636e72',
              fontSize: '14px',
              lineHeight: '1.5',
            }}
          >
            <li>Network connection issues</li>
            <li>Wallet connection problems</li>
            <li>Solana program not deployed</li>
            <li>Invalid IDL configuration</li>
          </ul>

          {this.state.error && (
            <details style={{ marginBottom: '16px' }}>
              <summary
                style={{
                  cursor: 'pointer',
                  color: '#d63031',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px',
                }}
              >
                Technical Details
              </summary>
              <div
                style={{
                  backgroundColor: '#f8f9fa',
                  padding: '12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  color: '#2d3436',
                  overflow: 'auto',
                  maxHeight: '200px',
                }}
              >
                <div>
                  <strong>Error:</strong> {this.state.error.message}
                </div>
                {this.state.error.stack && (
                  <div style={{ marginTop: '8px' }}>
                    <strong>Stack:</strong>
                    <pre
                      style={{ margin: '4px 0 0 0', whiteSpace: 'pre-wrap' }}
                    >
                      {this.state.error.stack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={this.handleRetry}
              style={{
                padding: '8px 16px',
                backgroundColor: '#00b894',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              Try Again
            </button>

            <button
              onClick={this.handleReload}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c5ce7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              Reload Page
            </button>
          </div>

          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#1976d2',
            }}
          >
            <strong>💡 Tip:</strong> If the problem persists, try refreshing the
            page or checking your wallet connection.
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export function useSolanaErrorHandler() {
  const [error, setError] = React.useState<Error | null>(null);

  const handleError = React.useCallback((error: Error) => {
    console.error('Solana error caught:', error);
    setError(error);
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    handleError,
    clearError,
  };
}

export default SolanaErrorBoundary;
